import { ChangeEvent, ReactElement, useEffect, useState } from "react";
import { uploadApis } from "@payping/api";
import type { CategoryCreateViewModel, CategoryDetailViewModel } from "@payping/api";
import { Add, CuToman, Delete, InfoCircleOutlined } from "@payping/icons";
import { createCategory, getCategories, removeCategory } from "@payping/redux/actions";
import {
  Button,
  Checkbox,
  Col,
  Divider,
  Flex,
  Input,
  InputNumber,
  LegacyForm,
  Modal,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
  Upload,
  message,
  useMessagedAsyncFn,
} from "@payping/web-components";
import type { UploadChangeParam, UploadFile } from "@payping/web-components/components/upload/types";
import { useAsync } from "react-use";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import styles from "@/assets/scss/Shop/Product/form.module.scss";
import { ProductFormProps } from "./types";
import { SwitchRow } from "@/core/component";
import { numberFormatter } from "@payping/utilities-shared/number";

const ProductForm = ({
  isDirectPaymentActive,
  formRef,
  loading,
  details,
  setProductImageLink,
  setProductImageUrl,
}: ProductFormProps) => {
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [newTag, setNewTag] = useState<string>("");
  const [modal, contextHolder] = Modal.useModal();
  const [isAddTagModalOpen, setIsAddTagModalOpen] = useState<boolean>(false);

  const categories = useAppSelector((state) => state.categories.list);
  const { loading: loadingCategories } = useAsync(() => dispatch(getCategories("")), [dispatch]);

  const [{ loading: loadingCreateTag }, createCategoryFn] = useMessagedAsyncFn(
    async (data: CategoryCreateViewModel): Promise<string> => dispatch(createCategory(data)),
    [dispatch],
    undefined,
    { success: false, useProgress: true },
  );

  useEffect(() => {
    if (details) {
      const { category, ...data } = details;
      formRef.setFieldsValue({
        ...data,
        categoryCode: category?.code,
      });
      if (details.imageLink) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setFileList([
          {
            name: "image",
            size: 0,
            type: "image/*",
            uid: "0",
            url: details.imageLink,
          },
        ]);
        setProductImageUrl(details.imageLink);
      }
    }
  }, [details, formRef, setProductImageUrl]);

  const uploadButton = (
    <div>
      <Add width={"14px"} className={"color--primary"} />
      <div className={"ant-upload-text"}>{t("product.form.upload.photo")}</div>
    </div>
  );

  const [, addTag] = useMessagedAsyncFn(
    async (): Promise<void> => {
      if (newTag) {
        const createdCategoryCode = await createCategoryFn({ name: newTag, products: [] });
        formRef.setFieldsValue({
          ...formRef.getFieldsValue(),
          categoryCode: createdCategoryCode,
        });
        setNewTag("");
        message.success({
          content: t("messages.success", { operation: t("common.create"), subject: t("category") }),
        });
      }
    },
    [newTag, createCategoryFn, formRef, t],
    undefined,
    { useProgress: true },
  );

  async function deleteTag(
    event: React.MouseEvent<SVGSVGElement, MouseEvent>,
    category: CategoryDetailViewModel,
  ): Promise<void> {
    event.stopPropagation();
    modal.confirm({
      cancelText: t("cancel"),
      content: t("messages.removeCategory", { subject: category.name }),
      mask: { closable: true },
      okText: t("ok"),
      onOk: async () => {
        if (category.code) {
          await dispatch(removeCategory(category.code));
          formRef.resetFields(["categoryCode"]);
        }
      },
    });
  }

  return (
    <Space orientation={"vertical"} className={"tw:w-full"}>
      {/* < تصویر محصول > */}
      <LegacyForm.Item name={"imageLink"} label={t("product.form.upload.label")}>
        <Upload
          disabled={loading}
          listType={"picture-card"}
          requestFunction={uploadApis.itemWithFilePost}
          onChange={(info: UploadChangeParam): void => {
            setProductImageLink(info?.file?.originFileObj);
            setFileList(info.fileList);
          }}
          fileList={fileList}
          showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
          allowedExtensions={["mp4", "jpg", "jpeg", "png"]}
          showTooltip
        >
          {fileList.length ? null : uploadButton}
        </Upload>
      </LegacyForm.Item>
      {/* < تصویر محصول /> */}

      {/* < نام محصول > */}
      <LegacyForm.Item label={t("product.name")} rules={[{ required: true }]} name={"title"}>
        <Input disabled={loading} />
      </LegacyForm.Item>
      {/* < نام محصول /> */}

      {/* < توضیحات محصول > */}
      <LegacyForm.Item label={t("description")} name={"description"}>
        <Input.TextArea disabled={loading} />
      </LegacyForm.Item>
      {/* < توضیحات محصول /> */}

      {/* <> */}
      <LegacyForm.Item
        label={t("product.form.price.title")}
        shouldUpdate={(prev, curr): boolean =>
          prev.defineAmountByUser !== curr.defineAmountByUser || prev.amount !== curr.amount
        }
      >
        {(form1): ReactElement => {
          const switchPriceByUser = form1.getFieldValue("defineAmountByUser");
          return (
            <Space size={"large"} orientation={"vertical"} className="tw:w-full">
              {/*  < قیمت محصول > */}
              <LegacyForm.Item
                className={"tw:mb-0!"}
                rules={[
                  { required: !switchPriceByUser },
                  {
                    validator: (_, value): Promise<void> =>
                      value && !(Number(value) >= 1000)
                        ? Promise.reject(t("error.minimum", { quantity: 1000 }))
                        : Promise.resolve(),
                  },
                ]}
                name={"amount"}
              >
                <InputNumber
                  formatter={(value) => numberFormatter.format(value, { maxDecimals: 2 })}
                  parser={(value) => numberFormatter.parse(value)}
                  disabled={switchPriceByUser || loading}
                  className="tw:w-full!"
                  suffix={<CuToman width={24} height={24} />}
                  inputMode="numeric"
                  changeOnWheel={false}
                  controls={false}
                  placeholder={t("product.form.price.placeHolder")}
                />
              </LegacyForm.Item>
              {/*  < قیمت محصول /> */}

              {/* < مالیات ارزش بر افزوده > */}
              <LegacyForm.Item noStyle name={"haveTax"} valuePropName={"checked"}>
                <Checkbox>{t("product.form.tax.label")}</Checkbox>
              </LegacyForm.Item>
              {/* < مالیات ارزش بر افزوده /> */}

              {/* < سویچ تعیین قیمت توسط مشتری > */}
              <LegacyForm.Item
                noStyle
                // this LegacyForm.Item is used inside LinkForm to enable or disable isMultiple value
                name={"defineAmountByUser"}
                valuePropName={"checked"}
              >
                <SwitchRow label={<Typography.Text>{t("product.form.price.byUser")}</Typography.Text>} />
              </LegacyForm.Item>
              {/* < سویچ تعیین قیمت توسط مشتری /> */}
            </Space>
          );
        }}
      </LegacyForm.Item>
      {/* </ > */}

      <LegacyForm.Item
        shouldUpdate={(prevValues, currentValues): boolean => prevValues.unlimited !== currentValues.unlimited}
      >
        {(form1): ReactElement => (
          <Flex vertical gap={4}>
            {t("quantity.title")}
            <Space.Compact className="tw:w-full">
              <LegacyForm.Item noStyle name={"unlimited"} rules={[{ required: true }]}>
                <Select disabled={loading} className={styles.groupSelect}>
                  <Select.Option value={false}>{t("product.form.limited")}</Select.Option>
                  <Select.Option value={true}>{t("product.form.unlimited")}</Select.Option>
                </Select>
              </LegacyForm.Item>
              {!form1.getFieldValue("unlimited") && (
                <LegacyForm.Item
                  noStyle
                  rules={[
                    { required: true },
                    {
                      validator: (_, value): Promise<void> =>
                        value && !(Number(value) >= 1)
                          ? Promise.reject(t("error.minimum", { quantity: 1 }))
                          : Promise.resolve(),
                    },
                  ]}
                  name={"quantity"}
                >
                  <InputNumber
                    disabled={loading}
                    className="tw:w-full!"
                    inputMode="numeric"
                    changeOnWheel={false}
                    controls={false}
                  />
                </LegacyForm.Item>
              )}
            </Space.Compact>
          </Flex>
        )}
      </LegacyForm.Item>

      {/* < سویچ تعیین قیمت توسط مشتری > */}
      <LegacyForm.Item name={"isMultiple"} valuePropName={"checked"}>
        {isDirectPaymentActive ? (
          <Checkbox disabled={formRef.getFieldValue("defineAmountByUser") || loading || !isDirectPaymentActive}>
            {t("product.form.perma.multiple")}
          </Checkbox>
        ) : (
          <Tooltip title={t("product.form.isMultipleDisabled")}>
            <Space>
              <InfoCircleOutlined width={14} className={"color--gray700"} />
              <Typography.Text className={"color--gray700"}>{t("product.form.perma.multiple")}</Typography.Text>
            </Space>
          </Tooltip>
        )}
      </LegacyForm.Item>

      {/* < سویچ تعیین قیمت توسط مشتری /> */}

      {/* < سکشن دسته بندی محصولات > */}
      <LegacyForm.Item label={t("product.form.tag.title")}>
        <Row gutter={[8, 8]}>
          <Col xs={24}>
            {/* < انتخاب یا ایجاد تگ جدید > */}
            <LegacyForm.Item noStyle name={"categoryCode"}>
              <Select
                placeholder={t("product.form.tag.select")}
                showSearch
                filterOption={(input, option): boolean => (option?.title as unknown as string).indexOf(input) >= 0}
                popupRender={(menu) => (
                  <>
                    <div className={"tw:w-full tw:py-0!"}>
                      <Button
                        type={"link"}
                        icon={<Add width={14} height={14} />}
                        className={"tw:w-full"}
                        onClick={() => setIsAddTagModalOpen(true)}
                      >
                        {t("product.form.tag.add")}
                      </Button>
                    </div>

                    <Divider className={"tw:m-0!"} />
                    {menu}
                  </>
                )}
                disabled={loadingCategories || loading}
                allowClear
                optionLabelProp={"title"}
                options={categories.map((category, index) => ({
                  key: index,
                  label: (
                    <Space className={styles.categoryOption}>
                      <Tooltip title={t("delete")}>
                        <Delete
                          className={styles.categoryDelete}
                          onClick={(event: React.MouseEvent<SVGSVGElement, MouseEvent>) => deleteTag(event, category)}
                        />
                      </Tooltip>
                      {category.name}
                    </Space>
                  ),
                  title: category.name,
                  value: category.code || "",
                }))}
              />
            </LegacyForm.Item>
            {/* < انتخاب یا ایجاد تگ جدید /> */}
          </Col>

          {/* < مودال ایجاد تگ جدید > */}
          <Modal
            title={t("product.form.tag.add")}
            open={isAddTagModalOpen}
            onCancel={() => setIsAddTagModalOpen(false)}
            okButtonProps={{ loading: loading || loadingCreateTag }}
            onOk={async () => {
              await addTag();
              setIsAddTagModalOpen(false);
            }}
          >
            <Input
              disabled={loading || loadingCreateTag}
              value={newTag}
              placeholder={t("product.form.tag.add")}
              onChange={(event: ChangeEvent<HTMLInputElement>): void => {
                setNewTag(event.target.value);
              }}
              autoFocus
            />
          </Modal>
          {/* < مودال ایجاد تگ جدید /> */}
        </Row>
      </LegacyForm.Item>

      {/* < سکشن دسته بندی محصولات /> */}

      {contextHolder}
    </Space>
  );
};

export default ProductForm;
